// Home v2 — desktop/mobile layout for the mobile-web (react-native-web) build.
// Webpack resolves this `.web.tsx` over `DashboardScreen.tsx`; native iOS/Android
// keeps using the `.tsx` (common dashboard) unchanged.
//
// Responsive tiers (see useHomeBreakpoints + the design):
//   >= 1200px : sidebar + two-column grid. Middle column scrolls; the right
//               column stays fixed (sticky) like the sidebar.
//   768–1199  : sidebar + single column (right column stacks under left).
//   < 768px   : bottom tab bar + single column; token detail inline.
import React, { useEffect, useState } from 'react'
import { Pressable, ScrollView, useWindowDimensions, View } from 'react-native'

import { useTranslation } from 'react-i18next'

import InstallAppPrompt from '@common/components/InstallAppPrompt'
import useNavigation from '@common/hooks/useNavigation'
import { consumeAddAccountReturnRoute } from '@common/modules/account-select/helpers/accountSwitcherReopen'
import { WEB_ROUTES } from '@common/modules/router/constants/common'
import { isSameChainSwapNetwork } from '@common/modules/swap-and-bridge/constants/swapNetworks'
import AccountSelectModal from '@mobile/modules/account-select/screens/AccountSelectScreen/AccountSelectModal'

import { useReceiveSheet } from '@common/contexts/receiveSheetContext'
import useToast from '@common/hooks/useToast'
import Collections from '@common/modules/dashboard/components/Collections'
import DeFiPositions from '@common/modules/dashboard/components/DeFiPositions'
import useRailgunContext from '@web/hooks/useRailgunContext'

import AppShell from './home/components/AppShell'
import AssetTable from './home/components/AssetTable'
import ActivityCard from './home/components/ActivityCard'
import BalanceCard from './home/components/BalanceCard'
import BatchProgressBanner from './home/components/BatchProgressBanner'
import HideTokenConfirmModal from './home/components/HideTokenConfirmModal'
import PendingRequestsBanner from './home/components/PendingRequestsBanner'
import PortfolioAllocation from './home/components/PortfolioAllocation'
import QuickActions from './home/components/QuickActions'
import TokenDetailPanel from './home/components/TokenDetailPanel'
import { NavItem } from './home/navConfig'
import { getSwapRole } from './home/swapSupport'
import { LAYOUT } from './home/tokens'
import { getFitsTwoColumn } from './home/twoColumn'
import useHomeBreakpoints from './home/useHomeBreakpoints'
import useHomeDashboard from './home/useHomeDashboard'

const noopScroll = () => {}

const DashboardScreen = () => {
  const layout = useHomeBreakpoints()
  const { isMobile } = layout
  // The two-column grid only renders when the content area (viewport minus the
  // sidebar) can actually hold it; otherwise the row overflowed and clipped the
  // asset table on small desktops.
  const isDesktop = getFitsTwoColumn(layout, LAYOUT.gridLeftMinWidth)
  const { width: windowWidth } = useWindowDimensions()
  const home = useHomeDashboard()
  const [isAccountSelectVisible, setIsAccountSelectVisible] = useState(false)
  // Set when returning from a backed-out add-account onboarding sub-flow, so the
  // switcher reopens with the "Add an account" popup already expanded.
  const [autoOpenAddAccount, setAutoOpenAddAccount] = useState(false)
  const { openReceive } = useReceiveSheet()
  const { navigate } = useNavigation()

  // Reopen the account switcher + Add-account popup after the user backs out of an
  // onboarding sub-flow that was launched from the dashboard switcher (mobile-web;
  // origin route recorded by the onboarding navigation).
  useEffect(() => {
    if (consumeAddAccountReturnRoute() === WEB_ROUTES.dashboard) {
      setAutoOpenAddAccount(true)
      setIsAccountSelectVisible(true)
    }
  }, [])
  const { addToast } = useToast()
  const { t } = useTranslation()
  const { isBalanceFetching } = useRailgunContext()

  const onNavigate = (item: NavItem) => {
    // Receive opens as an overlay popup, not a route change.
    if (item.key === 'receive') {
      openReceive()
      return
    }
    // Unshield needs a synced private balance — block + notify while syncing.
    if (item.key === 'unshield' && isBalanceFetching) {
      addToast(
        t('Your private balance is still syncing. Please wait until it finishes to unshield.'),
        { type: 'info' }
      )
      return
    }
    if (item.route) home.goTo(item.route)
  }

  // Preselect the token via URL params — TransferController reads
  // `view.searchParams` (address + chainId) to default the selected token.
  const goSend = () => {
    const a = home.selectedAsset
    home.goTo(a ? `${home.routes.transfer}?chainId=${a.chainId}&address=${a.address}` : home.routes.transfer)
  }
  const goReceive = () => openReceive()
  // Smart Swap routing. A public token on a chain with same-chain routing opens the
  // `/swap` screen (ZKX Trade) pre-filled on the FROM side — `swap-and-bridge` is
  // the Bridge form since the Swap/Bridge split, so sending it there opened Bridge
  // (Trello #494). Elsewhere (and in private mode) the LibertySwap classifier
  // decides: a supported source pre-fills FROM, a receive-only token pre-fills TO,
  // an unsupported token keeps the button clickable but surfaces a toast.
  const goSwap = () => {
    const a = home.selectedAsset
    if (!a) return
    if (!home.isPrivate && isSameChainSwapNetwork(a.chainId)) {
      navigate(WEB_ROUTES.swap, {
        state: { preselectedFromToken: { address: a.address, chainId: a.chainId } }
      })
      return
    }
    const swapRole = getSwapRole(a.address, a.chainId, home.isPrivate)
    if (swapRole === 'unsupported') {
      addToast(t('This token is not supported for swap or bridge.'), { type: 'info' })
      return
    }
    const preselected = { address: a.address, chainId: a.chainId }
    navigate(WEB_ROUTES.swapAndBridge, {
      state:
        swapRole === 'output'
          ? { preselectedToToken: preselected }
          : { preselectedFromToken: preselected }
    })
  }
  // Private mode replaces Swap with Unshield, pre-selecting the token on the
  // Unshield screen (UnshieldScreenBody reads `preselectedToken` from route state).
  const goUnshield = () => {
    const a = home.selectedAsset
    if (!a) return
    if (isBalanceFetching) {
      addToast(
        t('Your private balance is still syncing. Please wait until it finishes to unshield.'),
        { type: 'info' }
      )
      return
    }
    navigate(WEB_ROUTES.unshield, {
      state: { preselectedToken: { address: a.address, chainId: a.chainId } }
    })
  }
  const goActivity = () => home.goTo(home.routes.transactions)
  const goAddToken = () => home.goTo(home.routes.manageTokens)

  // NFTs / DeFi reuse the existing dashboard list components (previous-version
  // behavior) when their tab is active; tokens use the styled rows in AssetTable.
  let bodyOverride: React.ReactNode
  if (home.openTab === 'collectibles') {
    bodyOverride = (
      <Collections
        openTab={home.openTab}
        setOpenTab={home.setOpenTab}
        initTab={home.initTab}
        sessionId={home.sessionId}
        onScroll={noopScroll}
        networks={home.allNetworks}
        dashboardNetworkFilterName={home.dashboardNetworkFilterName}
        animatedOverviewHeight={home.animatedOverviewHeight}
        hideHeader
      />
    )
  } else if (home.openTab === 'defi') {
    bodyOverride = (
      <DeFiPositions
        openTab={home.openTab}
        setOpenTab={home.setOpenTab}
        initTab={home.initTab}
        sessionId={home.sessionId}
        onScroll={noopScroll}
        dashboardNetworkFilterName={home.dashboardNetworkFilterName}
        animatedOverviewHeight={home.animatedOverviewHeight}
        hideHeader
      />
    )
  }

  const balanceCard = (
    <BalanceCard
      mode={home.mode}
      onModeChange={home.handleModeChange}
      privateModeReason={home.privateModeReason}
      onRetryRailgunInit={home.ensureRailgunEngine}
      accountName={home.accountName}
      accountAddress={home.accountAddress}
      displayAddress={home.displayAddress}
      accountPfp={home.accountPfp}
      accountIsSmart={home.accountIsSmart}
      onAccountPress={() => setIsAccountSelectVisible(true)}
      balanceFormatted={home.balanceFormatted}
      isLoading={home.isBalanceLoading}
      isSyncing={home.isSyncing}
      showScanProgress={home.showScanProgress}
      scanningProgress={home.scanningProgress}
      showFirstBalanceLoadHint={home.showFirstBalanceLoadHint}
      shieldPending={home.shieldPending}
      totalMissingPoiUsd={home.totalMissingPoiUsd}
      showPrivatePendingRow={home.showPrivatePendingRow}
      hidden={home.hidden}
      onToggleHidden={home.toggleHidden}
      onRefresh={home.refresh}
      compact={isMobile}
    />
  )

  // Mobile tier only — tablet/desktop already expose these in the Sidebar.
  const quickActions = isMobile ? (
    <QuickActions
      mode={home.mode}
      privateModeReason={home.privateModeReason}
      onPress={onNavigate}
    />
  ) : null

  const assetTable = (
    <AssetTable
      assets={home.assets}
      dustAssets={home.dustAssets}
      dustTotalUSD={home.dustTotalUSD}
      isLoading={home.isTokensLoading}
      mode={home.mode}
      selectedId={home.selectedTokenId}
      onSelect={home.selectToken}
      onAddToken={goAddToken}
      assetType={home.openTab}
      onAssetTypeChange={home.setOpenTab}
      searchControl={home.searchControl}
      onSearchCleared={home.clearSearch}
      bodyOverride={bodyOverride}
      privateError={home.privateBalanceError}
      pendingVerification={home.isVerifyingPrivateBalance}
      onRetry={home.refresh}
      networkFilterName={home.dashboardNetworkFilterName}
      hidden={home.hidden}
    />
  )

  const privacyDonut = (
    <PortfolioAllocation
      assets={home.assets}
      totalUsd={home.isPrivate ? home.privateTotal : home.publicTotal}
      hidden={home.hidden}
      loading={home.isBalanceLoading || home.isTokensLoading}
    />
  )
  const activityCard = <ActivityCard items={home.recentActivity} onSeeAll={goActivity} />
  // Pending sign-request bar (e.g. batched tx "waiting to be signed"); only the
  // entry point for executionType: 'queue' requests. Renders null when empty.
  const pendingBanner = <PendingRequestsBanner />
  // POI batch progress strip ("Processing POI batch …") between the balance card
  // and the token list; private mode only, renders null when no active batch.
  const batchBanner = <BatchProgressBanner mode={home.mode} />

  const tokenDetail = home.selectedAsset ? (
    <TokenDetailPanel
      asset={home.selectedAsset}
      mode={home.mode}
      embedded={false}
      hidden={home.hidden}
      onHide={home.requestHideSelectedToken}
      onSend={goSend}
      onReceive={goReceive}
      onSwap={goSwap}
      onUnshield={goUnshield}
    />
  ) : null

  // Right column: token detail replaces donut/activity when an asset is selected.
  const rightColumn = home.selectedAsset ? (
    tokenDetail
  ) : (
    <>
      {privacyDonut}
      {!home.isPrivate && activityCard}
    </>
  )

  return (
    <>
    <AppShell active="home" mode={home.mode} layout={layout} onNavigate={onNavigate} twoColumn={isDesktop}>
      {isDesktop ? (
        // Center the grid; left column scrolls, right column is fixed/sticky.
        <View style={{ flex: 1, alignItems: 'center' }}>
          <View
            style={{
              flex: 1,
              flexDirection: 'row',
              gap: LAYOUT.gridGap,
              width: '100%',
              maxWidth: LAYOUT.contentMaxWidth,
              paddingHorizontal: 24
            }}
          >
            <ScrollView
              // Grows to the design width but gives way to the fixed right
              // column on narrow desktops instead of overflowing the row.
              style={{ flex: 1, minWidth: 0, maxWidth: LAYOUT.gridLeftWidth }}
              contentContainerStyle={{ paddingVertical: 40, gap: 16 }}
              showsVerticalScrollIndicator={false}
            >
              {balanceCard}
              {pendingBanner}
              {batchBanner}
              {assetTable}
            </ScrollView>
            <View style={{ width: LAYOUT.gridRightWidth, paddingVertical: 40, gap: 16 }}>
              {rightColumn}
            </View>
          </View>
        </View>
      ) : (
        // Tablet (stacked) + mobile: single column inside AppShell's ScrollView.
        <View style={{ gap: 16 }}>
          {balanceCard}
          {quickActions}
          {pendingBanner}
          {batchBanner}
          {assetTable}
          {/* Mobile shows the detail in a popup (below); tablet keeps it inline. */}
          {!isMobile && home.selectedAsset ? tokenDetail : null}
          {privacyDonut}
          {!home.isPrivate && activityCard}
        </View>
      )}
      {home.isPrivate && <InstallAppPrompt />}
    </AppShell>
    {/* Mobile: token detail as a centered popup over a dimmed backdrop. */}
    {isMobile && home.selectedAsset ? (
      <View
        style={[
          { position: 'fixed' as any, inset: 0 as any, alignItems: 'center', justifyContent: 'center' }
        ]}
      >
        <Pressable
          style={{ position: 'absolute' as any, inset: 0 as any, backgroundColor: 'rgba(0,0,0,0.5)' }}
          onPress={home.clearSelectedToken}
        />
        <ScrollView
          style={{ width: Math.min(420, windowWidth * 0.92), maxHeight: '90%', flexGrow: 0 }}
          showsVerticalScrollIndicator={false}
        >
          {tokenDetail}
        </ScrollView>
      </View>
    ) : null}
    <HideTokenConfirmModal
      visible={home.hideTokenModalVisible}
      mode={home.mode}
      onCancel={home.cancelHideToken}
      onConfirm={home.confirmHideToken}
    />
    <AccountSelectModal
      isVisible={isAccountSelectVisible}
      autoOpenAddAccount={autoOpenAddAccount}
      onClose={() => {
        setIsAccountSelectVisible(false)
        setAutoOpenAddAccount(false)
      }}
    />
    </>
  )
}

export default React.memo(DashboardScreen)
